import React from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export interface SearchInputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
    /**
     * Optional node rendered inside the input on the right (e.g. an attachment /
     * paperclip button). Absolutely positioned so it overlays the input padding.
     */
    attachmentSlot?: React.ReactNode;
    /**
     * Optional node rendered as a sibling after the input (e.g. a submit button).
     * Use the shared `ui/button.tsx` here to keep the app-standard look.
     */
    actionButton?: React.ReactNode;
    /** Extra classes for the input element itself. */
    inputClassName?: string;
    /** Extra classes for the outer flex row wrapper. */
    className?: string;
}

/**
 * App-standard search input: pill-shaped (`rounded-full`), a 1px `border-line`
 * border, a left magnifying-glass icon and a purple `ring-accent` focus ring.
 * Mirrors the look used across the app (e.g. PromptManager) and is the single
 * reusable home for that markup.
 *
 * Supports an optional trailing `actionButton` and an in-input `attachmentSlot`
 * so callers can keep a submit button and/or a paperclip without re-rolling the
 * input chrome.
 */
export const SearchInput: React.FC<SearchInputProps> = ({
    attachmentSlot,
    actionButton,
    inputClassName = '',
    className = '',
    disabled,
    ...inputProps
}) => {
    // Reserve room on the right for the attachment slot when present.
    const rightPadding = attachmentSlot ? 'pr-12' : 'pr-4';
    return (
        <div className={`flex items-center gap-3 ${className}`}>
            <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-muted pointer-events-none" />
                <input
                    type="text"
                    disabled={disabled}
                    className={`w-full pl-10 ${rightPadding} py-2 border border-line rounded-full bg-surface text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all disabled:opacity-50 disabled:cursor-not-allowed ${inputClassName}`}
                    {...inputProps}
                />
                {attachmentSlot}
            </div>
            {actionButton}
        </div>
    );
};

export default SearchInput;
